import { createAmglobalHttpClient } from '@amglobal/api-client'
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { useEffect, useMemo, type ReactNode } from 'react'
import { Toaster } from 'sonner'
import { getPublicEnv } from '@/config/public-env'
import { SessionRefreshWatcher } from '@/features/auth/components/session-refresh-watcher'
import { resolveWorkspaceAccessToken } from '@/features/auth/session/workspace-session'
import { HttpClientProvider } from '@/shared/api/http-client-context'
import { registerWorkspaceAuthInterceptors } from '@/shared/api/auth-interceptor'
import { registerSessionExpiredHandler } from '@/shared/auth/session-expired'
import { AppErrorFallback } from '@/shared/components/app-error-fallback'
import { ErrorBoundary } from '@/shared/components/error-boundary'
import { captureApiError } from '@/shared/observability/sentry'

export function AppProviders({ children }: { children: ReactNode }) {
  const queryClient = useMemo(
    () =>
      new QueryClient({
        queryCache: new QueryCache({
          onError: (error) => captureApiError(error),
        }),
        mutationCache: new MutationCache({
          onError: (error, _variables, _context, mutation) => {
            const skipSentry =
              mutation.meta != null &&
              typeof mutation.meta === 'object' &&
              'skipSentry' in mutation.meta &&
              mutation.meta.skipSentry === true
            captureApiError(error, { skipSentry })
          },
        }),
        defaultOptions: {
          queries: {
            staleTime: 30_000,
            retry: (failureCount, error) => {
              const status = (error as { response?: { status?: number } })?.response?.status
              if (status === 429 || status === 401 || status === 403 || status === 404) return false
              return failureCount < 1
            },
          },
          mutations: {
            retry: (failureCount, error) => {
              const status = (error as { response?: { status?: number } })?.response?.status
              // Never retry 429 — a second hit in the same window keeps the user locked out.
              if (status === 429 || status === 401 || status === 403 || status === 404) return false
              return failureCount < 1
            },
          },
        },
      }),
    [],
  )

  const env = getPublicEnv()
  const httpClient = useMemo(
    () =>
      createAmglobalHttpClient({
        baseURL: env.apiBaseUrl.replace(/\/$/, ''),
        getAccessToken: () => resolveWorkspaceAccessToken(),
      }),
    [env.apiBaseUrl],
  )

  useEffect(() => {
    return registerWorkspaceAuthInterceptors(httpClient)
  }, [httpClient])

  useEffect(() => {
    return registerSessionExpiredHandler({
      clearQueries: () => queryClient.clear(),
    })
  }, [queryClient])

  return (
    <QueryClientProvider client={queryClient}>
      <HttpClientProvider client={httpClient}>
        <ErrorBoundary fallback={(error) => <AppErrorFallback error={error} />}>
          <SessionRefreshWatcher />
          {children}
        </ErrorBoundary>
        <Toaster richColors position="top-center" />
      </HttpClientProvider>
      {import.meta.env.DEV ? (
        <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />
      ) : null}
    </QueryClientProvider>
  )
}
